'use client';

import type { ReactNode } from 'react';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { isEqual, noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';
import { EMPTY_ARRAY } from '@frameio/utils';

type ComparisonAssetsContextType = {
  setComparisonAssetIds: (assetIds: string[]) => void;
  comparisonAssetIds: string[];
  activeMediaStateIndex: number;
  setActiveMediaStateIndex: (index: number) => void;
  isLinked: boolean;
  toggleIsLinked: () => void;
};

const ComparisonAssetsContext = createContext<ComparisonAssetsContextType>({
  setComparisonAssetIds: noop,
  comparisonAssetIds: [],
  activeMediaStateIndex: 0,
  setActiveMediaStateIndex: noop,
  isLinked: false,
  toggleIsLinked: noop,
});

export const ComparisonAssetsContextProvider = ({
  children,
  assetIdsFromProps = EMPTY_ARRAY,
  activeIndex = 0,
}: {
  children: ReactNode;
  assetIdsFromProps?: string[];
  activeIndex?: number;
}) => {
  const [comparisonAssetIds, setComparisonAssetIds] =
    useState(assetIdsFromProps);
  const [activeMediaStateIndex, setActiveMediaStateIndex] =
    useState(activeIndex);
  const [isLinked, toggleIsLinked] = useReducer((linked) => !linked, true);

  const value = useMemo(
    () => ({
      setComparisonAssetIds,
      comparisonAssetIds,
      activeMediaStateIndex,
      setActiveMediaStateIndex,
      isLinked,
      toggleIsLinked,
    }),
    [comparisonAssetIds, activeMediaStateIndex, isLinked]
  );

  useEffect(() => {
    if (!isEqual(comparisonAssetIds, assetIdsFromProps)) {
      setComparisonAssetIds(assetIdsFromProps);

      if (activeMediaStateIndex > 0 && assetIdsFromProps?.length === 0) {
        setActiveMediaStateIndex(0);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetIdsFromProps]);

  return (
    <ComparisonAssetsContext.Provider value={value}>
      {children}
    </ComparisonAssetsContext.Provider>
  );
};

export const useComparisonAssetIds = () => {
  return useContextSelector(
    ComparisonAssetsContext,
    ({ comparisonAssetIds }) => comparisonAssetIds
  );
};

export const useSetComparisonAssetIds = () => {
  return useContextSelector(
    ComparisonAssetsContext,
    ({ setComparisonAssetIds }) => setComparisonAssetIds
  );
};

export const useSetActiveMediaStateIndex = () => {
  return useContextSelector(
    ComparisonAssetsContext,
    ({ setActiveMediaStateIndex }) => setActiveMediaStateIndex
  );
};

export const useActiveMediaStateIndex = () => {
  return useContextSelector(
    ComparisonAssetsContext,
    ({ activeMediaStateIndex }) => activeMediaStateIndex || 0
  );
};

export const useIsLinked = () =>
  useContextSelector(ComparisonAssetsContext, ({ isLinked }) => isLinked);

export const useToggleIsLinked = () =>
  useContextSelector(
    ComparisonAssetsContext,
    ({ toggleIsLinked }) => toggleIsLinked
  );
